import { useCallback, useRef } from "react"
import { useDownloadQuotationDetailsWord } from "hooks/api/quotation/quotations.hook"
import { useMessage } from "hooks/message.hook"
import { DETAILS_WORD_MESSAGES } from "helpers/detailsWord.constants"
import type { MutableRefObject } from "react"

type Props = {
	// Read at click time rather than captured: see the identity note below.
	quotationIdRef: MutableRefObject<number | undefined>
	// Parts of the download name. Passed as plain values and mirrored into a ref below, so the menu
	// picks up whatever has loaded by the time it is clicked. Both are `undefined` only while their
	// query is unsettled — never because a quotation lacks a name (the API substitutes "Untitled").
	quotationName?: string
	drawingName?: string | null
}

// The editor's entry point for the on-demand Quotation Details Word file (#1448). Mirrors
// useResultCsvDownload.ts line for line: the burger menu is rendered ONCE into the Apryse shadow
// DOM by a `customElement.render()` callback, so whatever function it receives is captured for the
// lifetime of the viewer. This returns a callback with a permanently stable identity that reads the
// current quotation id from a ref, which is what makes that capture safe.
export const useDetailsWordDownload = ({ quotationIdRef, quotationName, drawingName }: Props) => {
	const { downloadDetailsWord: downloadWord } = useDownloadQuotationDetailsWord()
	const { error: errorMessage } = useMessage()

	const namesRef = useRef({ quotationName, drawingName })
	namesRef.current = { quotationName, drawingName }
	const downloadWordRef = useRef(downloadWord)
	downloadWordRef.current = downloadWord
	const errorMessageRef = useRef(errorMessage)
	errorMessageRef.current = errorMessage

	const downloadDetailsWord = useCallback(async () => {
		const quotationId = quotationIdRef.current
		const { quotationName, drawingName } = namesRef.current
		if (quotationId == null || !quotationName) {
			// Shouldn't happen — the editor is only reachable with a quotation in the URL, and opening it
			// prefetches the quotation via `ensureQueryData` — but a menu click must never be a silent
			// no-op, and the name is not something to invent.
			errorMessageRef.current(DETAILS_WORD_MESSAGES.failed)
			return
		}
		await downloadWordRef.current({ quotationId, quotationName, drawingName })
	}, [quotationIdRef])

	return { downloadDetailsWord }
}
